/**
 * ----------------------------------------------------------------------------
 * SOLD-OUT SWAP
 * ----------------------------------------------------------------------------
 * Drives the [data-product-swap] block product-info renders under the Sold out
 * button when every variant is sold out:
 *
 * 1. Fill it. One request to /recommendations/products with
 *    section_id=product-swap and the related intent returns the swap card for
 *    the best in-stock product and "In stock now, similar to this" (see
 *    sections/product-swap.liquid). The card goes in only when Liquid did not
 *    already render one from the product's in-stock colour siblings; the list
 *    goes in either way, minus anything the card already offers. The fetched
 *    markup is our own section render, moved in with DOMParser + importNode,
 *    never innerHTML; scripts in it do not run.
 *
 * 2. Tick the matches. The related card is rendered without the sold-out
 *    product, so its spec chips arrive unchecked; each chip whose value equals
 *    the sold-out product's (data-source-specs) gets .is-match, and two or
 *    more turn "Highlights" into "Matches ... on".
 *
 * 3. Colour pills: show the picked colour's option, hide the rest.
 *
 * 4. Turn the mobile sticky bar into the swap: "Swap to Dolly Blue", the swap's
 *    price and photo, and an Add button wearing data-also-buy-add so
 *    product-buybox.js posts the swap's variant (not the sold-out form).
 *    data-swap-active tells product-buybox.js to stop mirroring the sold-out
 *    variant into the bar.
 *
 * The add buttons themselves are product-buybox.js's: everything here carries
 * data-also-buy-add and data-variant-id.
 *
 * Loaded with defer on the product template only (layout/theme.liquid), and a
 * no-op wherever [data-product-swap] is absent.
 */
(function () {
  'use strict';

  function sourceSpecs(root) {
    try {
      return JSON.parse(root.getAttribute('data-source-specs') || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  function markMatches(root) {
    var specs = sourceSpecs(root);
    var options = root.querySelectorAll('[data-swap-option]');

    for (var i = 0; i < options.length; i++) {
      var chips = options[i].querySelectorAll('[data-spec-key]');
      var matches = 0;

      for (var j = 0; j < chips.length; j++) {
        var key = chips[j].getAttribute('data-spec-key');
        var match = Object.prototype.hasOwnProperty.call(specs, key) &&
          String(specs[key]).trim().toLowerCase() === String(chips[j].getAttribute('data-spec-value')).trim().toLowerCase();
        chips[j].classList.toggle('is-match', match);
        if (match) matches++;
      }

      var title = options[i].querySelector('[data-swap-specs-title]');
      var text = root.getAttribute('data-text-matches');
      if (title && text && matches >= 2) title.textContent = text;
    }
  }

  function visibleOption(root) {
    var options = root.querySelectorAll('[data-swap-option]');
    for (var i = 0; i < options.length; i++) {
      if (!options[i].hidden) return options[i];
    }
    return null;
  }

  function syncStickyBar(root) {
    var bar = document.querySelector('[data-product-sticky-bar]');
    var option = visibleOption(root);
    if (!bar || !option) return;

    var button = bar.querySelector('[data-sticky-bar-button]');
    var title = bar.querySelector('.product-sticky-bar__title');
    var prices = bar.querySelector('[data-sticky-bar-prices]');
    var deal = bar.querySelector('[data-sticky-bar-deal-prices]');
    var image = bar.querySelector('.product-sticky-bar__image');
    var name = option.getAttribute('data-name') || '';

    bar.setAttribute('data-swap-active', '');

    if (title) {
      var template = root.getAttribute('data-text-swap-to') || '[name]';
      title.textContent = template.split('[name]').join(name);
    }
    if (prices) {
      prices.classList.remove('brand-deal-standard');
      prices.textContent = '';
      var price = document.createElement('span');
      price.className = 'product-sticky-bar__price';
      price.textContent = option.getAttribute('data-price') || '';
      prices.appendChild(price);
    }
    if (deal) deal.hidden = true;
    if (image && option.getAttribute('data-thumb')) image.src = option.getAttribute('data-thumb');

    if (button) {
      button.disabled = false;
      button.removeAttribute('data-action');
      button.removeAttribute('data-zero-price');
      button.classList.remove('button--disabled');
      button.classList.add('button--primary');
      button.setAttribute('data-also-buy-add', '');
      button.setAttribute('data-variant-id', option.getAttribute('data-variant-id'));
      button.textContent = root.getAttribute('data-text-add') || 'Add';
    }
  }

  function revealOffer(root) {
    var offer = root.querySelector('[data-swap-offer]');
    if (offer && root.querySelector('[data-swap-option]')) offer.hidden = false;
  }

  function onPillClick(event) {
    var pill = event.target.closest('[data-swap-pick]');
    if (!pill) return;
    var root = pill.closest('[data-product-swap]');
    if (!root) return;

    var id = pill.getAttribute('data-swap-pick');
    var pills = root.querySelectorAll('[data-swap-pick]');
    for (var i = 0; i < pills.length; i++) {
      pills[i].setAttribute('aria-pressed', pills[i] === pill ? 'true' : 'false');
    }
    var options = root.querySelectorAll('[data-swap-option]');
    for (var j = 0; j < options.length; j++) {
      options[j].hidden = options[j].getAttribute('data-product-id') !== id;
    }
    syncStickyBar(root);
  }

  function offeredIds(root) {
    var ids = {};
    var options = root.querySelectorAll('[data-swap-option]');
    for (var i = 0; i < options.length; i++) ids[options[i].getAttribute('data-product-id')] = true;
    return ids;
  }

  function fill(root) {
    var slot = root.querySelector('[data-swap-slot]');
    var moreSlot = root.querySelector('[data-swap-more-slot]');
    var base = root.getAttribute('data-recommendations-url') || '/recommendations/products';
    var url = base + '?section_id=product-swap&intent=related&limit=8&product_id=' +
      encodeURIComponent(root.getAttribute('data-product-id'));

    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.text() : ''; })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');

        if (slot && !slot.querySelector('[data-swap-card]')) {
          var card = doc.querySelector('[data-swap-best] [data-swap-card]');
          if (card) slot.appendChild(document.importNode(card, true));
        }

        var more = doc.querySelector('[data-swap-more]');
        if (more && moreSlot) {
          var offered = offeredIds(root);
          var items = more.querySelectorAll('[data-swap-more-item]');
          var kept = 0;
          for (var i = 0; i < items.length; i++) {
            if (offered[items[i].getAttribute('data-product-id')]) {
              items[i].parentNode.removeChild(items[i]);
            } else {
              kept++;
            }
          }
          if (kept > 0) moreSlot.appendChild(document.importNode(more, true));
        }
      })
      .catch(function () { /* the block stays as rendered */ });
  }

  function init() {
    var root = document.querySelector('[data-product-swap]');
    if (!root) return;

    root.addEventListener('click', onPillClick);

    function settle() {
      markMatches(root);
      revealOffer(root);
      syncStickyBar(root);
    }

    // A server-rendered colour card is usable at once; the fetch only adds.
    if (root.querySelector('[data-swap-option]')) settle();
    fill(root).then(settle);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
